<template>
    <div class="rounded-xl shadow-lg">
        <div class="flex flex-wrap rounded-t-xl bg-purple px-5 py-6">
            <div class="saving-percentage-banner relative -mt-9 h-[6.5rem] w-24 rounded-t-xl bg-gold">
                <span class="absolute inset-x-0 top-4 text-center text-lg font-bold uppercase text-white"
                    >Save<br /><span class="text-2xl">{{ selectedPackProduct.percent_saving }}</span></span
                >
            </div>
            <div class="ml-4 flex flex-1 flex-col space-y-2 text-white">
                <span class="text-4.5xl font-semibold md:text-5xl">{{ selectedPackProduct.price }}</span>
                <span class="text-lg md:text-xl"
                    ><span class="line-through">{{ classicThankboxPrice }}</span
                    ><span class="ml-2 text-vanilla"
                        ><strong>{{ selectedPackProduct.price_per_card }}</strong> per card
                        <template v-if="showPricesWithVat">(inc. VAT)</template>
                    </span>
                </span>
            </div>
            <div class="flex flex-col space-y-1 max-md:mt-4 max-md:w-full">
                <span class="text-lg font-semibold text-white">Choose size:</span>
                <Multiselect
                    class="smaller-tag w-full sm:w-72"
                    :allow-empty="false"
                    :searchable="false"
                    v-model="selectedPackProduct"
                    :options="packProducts"
                    :show-labels="false"
                >
                    <template #singleLabel="{ option }">
                        <span class="text-xl md:text-1.5xl"
                            >Pack of <strong>{{ option.total_credits }}</strong></span
                        >
                    </template>
                    <template #option="{ option }">
                        <span class="text-lg md:text-xl"
                            >Pack of <strong>{{ option.total_credits }}</strong> -
                            <strong>{{ option.price }}</strong></span
                        >
                    </template>
                </Multiselect>
            </div>
        </div>
        <div class="relative rounded-b-xl bg-light-gray px-6 pb-8 pt-4 text-dark-gray md:px-8 md:text-lg">
            <img
                src="/img/public-page-assets/birthday-calendar-illustration.svg"
                alt="Birthday calendar illustration"
                width="700"
                height="574"
                class="absolute inset-y-0 right-0 my-auto w-1/2 max-md:hidden"
            />

            <p><strong>Premium</strong> features for <strong>less</strong> than the cost of Classic!</p>
            <ul class="fa-ul icon-bullet-list gold mt-4 space-y-2">
                <li v-for="feature in sharedFeatures" :key="feature">
                    <i class="fa-solid fa-check-circle fa-li"></i>
                    <span class="ml-2" v-html="feature" />
                </li>
                <li>
                    <i class="fa-solid fa-check-circle fa-li"></i>
                    <span class="ml-2" :class="{ 'border-b-2 border-b-green': selectedPackProduct.months_valid > 12 }"
                        >Valid for <strong>{{ selectedPackProduct.months_valid }} months</strong></span
                    >
                </li>
            </ul>
            <p class="mt-4 text-sm font-bold text-purple" v-if="selectedPackProduct.extra_trees > 0">
                <i class="fa-solid fa-tree mr-1"></i>
                +{{ selectedPackProduct.extra_trees }}
                {{ selectedPackProduct.extra_trees > 1 ? 'trees' : 'tree' }} planted with this plan!
            </p>
            <div class="mt-6 flex gap-2 max-sm:flex-col">
                <a
                    class="button px-12"
                    :href="`/app/buy-pack/${selectedPackProduct.id}`"
                    @click="trackFathomGoal('YPPIU3FU')"
                >
                    Buy pack
                </a>
                <a href="#compare-features" class="button secondary px-6">Compare features</a>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { ref } from 'vue'
import Multiselect from 'vue-multiselect'
import trackFathomGoal from '@/util/trackFathomGoal'
import { DetailedPackProductOption } from 'resources/js/types'

const sharedFeatures = [
    '<strong>One payment</strong>, one invoice',
    '<strong>Advanced</strong> background customisations',
    'Add <strong>videos</strong> and <strong>voice notes</strong> to messages',
    'Extra <strong>fonts</strong> for messages',
    'Create & schedule <strong>multiple Thankboxes</strong>',
    '<strong>Share access</strong> with your team',
]

const props = withDefaults(
    defineProps<{
        packProducts: DetailedPackProductOption[]
        classicThankboxPrice: string
        showPricesWithVat: boolean
        showCheapestFirst?: boolean
    }>(),
    {
        showCheapestFirst: false,
    },
)

const selectedPackProduct = ref<DetailedPackProductOption>(
    props.showCheapestFirst
        ? props.packProducts.sort((a, b) => a.total_credits - b.total_credits)[0]
        : props.packProducts.find(p => p.is_most_popular) || props.packProducts[0],
)
</script>
<style scoped>
.saving-percentage-banner::after {
    content: '';
    position: absolute;
    bottom: 0;
    border-left: 3rem solid transparent; /* Half the width of the tail */
    border-right: 3rem solid transparent; /* Half the width of the tail */
    border-bottom: 20px solid #5a499c; /* The color should match the banner's background-color */
}
</style>
